import { Extension, getSchemaTypeByName, getExtensionField, findParentNode } from '@tiptap/core';

const getTargetBlockNode = (state, specialMappings) => {
  if (specialMappings && specialMappings.length > 0) {
    const ancestorNames = specialMappings.map((m) => m.ancestor);
    const specialAncestor = findParentNode((node) => {
      return ancestorNames.includes(node.type.name);
    })(state.selection);
    if (specialAncestor) {
      const mapping = specialMappings.find((m) => m.ancestor === specialAncestor.node.type.name);
      if (mapping) {
        const targetNode = findParentNode((node) => {
          return node.type.name === mapping.target;
        })(state.selection);
        if (targetNode) {
          return targetNode;
        }
      }
    }
  }
  return findParentNode((node) => node.isBlock && node !== state.doc)(state.selection);
};
const findDefaultHandlers = (editor) => {
  const handlers = {};
  for (const extension of editor.extensionManager.extensions) {
    if (extension.name === "indentHandler") {
      continue;
    }
    if (extension.config.addKeyboardShortcuts) {
      const context = {
        name: extension.name,
        options: extension.options,
        storage: extension.storage,
        editor,
        type: getSchemaTypeByName(extension.name, editor.schema)
      };
      const addShortcuts = getExtensionField(extension, "addKeyboardShortcuts", context);
      const keyboardShortcuts = addShortcuts();
      const hasIndent = keyboardShortcuts && keyboardShortcuts["Tab"];
      const hasOutdent = keyboardShortcuts && keyboardShortcuts["Shift-Tab"];
      if (hasIndent || hasOutdent) {
        handlers[extension.name] = {
          indent: hasIndent,
          outdent: hasOutdent
        };
      }
    }
  }
  return handlers;
};
const getIndentHandler = (type, editor) => {
  const extension = editor.extensionManager.extensions.find((ext) => ext.name === type);
  if (!extension) {
    return null;
  }
  const clonedExtension = {
    ...extension
  };
  const context = {
    name: clonedExtension.name,
    options: clonedExtension.options,
    storage: clonedExtension.storage,
    editor,
    type: getSchemaTypeByName(clonedExtension.name, editor.schema)
  };
  const addShortcuts = getExtensionField(clonedExtension, "addKeyboardShortcuts", context);
  const keyboardShortcuts = addShortcuts();
  const hasIndent = keyboardShortcuts && keyboardShortcuts["Tab"];
  const hasOutdent = keyboardShortcuts && keyboardShortcuts["Shift-Tab"];
  if (!hasIndent && !hasOutdent) {
    return null;
  }
  return {
    indent: hasIndent ? keyboardShortcuts["Tab"] : void 0,
    outdent: hasOutdent ? keyboardShortcuts["Shift-Tab"] : void 0
  };
};
const defaultSpecialHandlers = {
  // Handle special case for dropdownItem
  dropdownItem: {
    // When indenting into a dropdownItem, check if we need to create dropdownItemContent
    indent: ({ currentNode, node, state, chain }) => {
      const previousNode = node.node;
      if (previousNode.type.name === "dropdownItem") {
        let hasContent = false;
        previousNode.content.forEach((child) => {
          if (child.type.name === "dropdownItemContent") {
            hasContent = true;
          }
        });
        if (!hasContent) {
          const dropdownItemContentType = state.schema.nodes.dropdownItemContent;
          if (!dropdownItemContentType) return false;
          const newContent = dropdownItemContentType.create(null, [
            currentNode.node
          ]);
          let insertPos = node.pos + 1;
          let foundTitle = false;
          previousNode.content.forEach((child) => {
            if (child.type.name === "dropdownItemTitle") {
              insertPos += child.nodeSize;
              foundTitle = true;
            }
          });
          if (!foundTitle) return false;
          return chain().deleteNode(currentNode.node.type).insertContentAt(insertPos, newContent).run();
        }
      }
      return false;
    }
  }
};
const IndentHandler = Extension.create({
  name: "indentHandler",
  priority: 1e3,
  addOptions() {
    return {
      specialMappings: [
        // Default example: if dropdownItemTitle is an ancestor, target the dropdownItem
        {
          ancestor: "dropdownItemTitle",
          target: "dropdownItem"
        }
      ],
      specialHandlers: defaultSpecialHandlers
    };
  },
  addCommands() {
    let handlers = {};
    let extensionsLength = 0;
    return {
      indent: () => ({ chain, commands, state, editor, can }) => {
        if (extensionsLength !== editor.extensionManager.extensions.length) {
          handlers = findDefaultHandlers(editor);
          extensionsLength = editor.extensionManager.extensions.length;
        }
        for (const key in handlers) {
          if (!editor.isActive(key)) {
            continue;
          }
          const modifiedEditor = {
            ...editor,
            chain,
            commands,
            can,
            fake: true
          };
          const indentFn = getIndentHandler(key, modifiedEditor)?.indent;
          if (!indentFn) {
            continue;
          }
          const result = indentFn({
            editor: modifiedEditor
          });
          if (result) {
            return true;
          }
        }
        const currentNode = getTargetBlockNode(state, this.options.specialMappings);
        if (!currentNode) return false;
        const $pos = state.doc.resolve(currentNode.pos + currentNode.depth);
        const parentNode = $pos.node($pos.depth - 1);
        const index = $pos.index($pos.depth - 1);
        if (index === 0) return false;
        const previousNode = parentNode.child(index - 1);
        let previousNodePos = $pos.start($pos.depth - 1);
        for (let i = 0; i < index - 1; i++) {
          previousNodePos += parentNode.child(i).nodeSize;
        }
        if (this.options.specialHandlers && this.options.specialHandlers[previousNode.type.name]?.indent) {
          const specialHandler = this.options.specialHandlers[previousNode.type.name].indent;
          const previousNodeWithPos = {
            node: previousNode,
            pos: previousNodePos
          };
          const result = specialHandler?.({
            currentNode,
            node: previousNodeWithPos,
            state,
            chain,
            editor
          });
          if (result) {
            return true;
          }
        }
        if (previousNode.type.contentMatch.matchType(currentNode.node.type)) {
          return chain().deleteNode(currentNode.node.type).insertContentAt(previousNodePos + previousNode.nodeSize - 1, currentNode.node.content).run();
        }
        if (previousNode.childCount > 0) {
          const lastChild = previousNode.child(previousNode.childCount - 1);
          if (lastChild.type.contentMatch.matchType(currentNode.node.type)) {
            const lastChildPos = previousNodePos + previousNode.nodeSize - lastChild.nodeSize - 1;
            return chain().deleteNode(currentNode.node.type).insertContentAt(lastChildPos + lastChild.nodeSize - 1, currentNode.node.content).run();
          }
        }
        return false;
      },
      outdent: () => ({ chain, commands, state, editor, can }) => {
        if (extensionsLength !== editor.extensionManager.extensions.length) {
          handlers = findDefaultHandlers(editor);
          extensionsLength = editor.extensionManager.extensions.length;
        }
        for (const key in handlers) {
          if (!editor.isActive(key)) {
            continue;
          }
          const modifiedEditor = {
            ...editor,
            chain,
            commands,
            can,
            fake: true
          };
          const outdentFn = getIndentHandler(key, modifiedEditor)?.outdent;
          if (!outdentFn) {
            continue;
          }
          const result = outdentFn({
            editor: modifiedEditor
          });
          if (result) {
            return true;
          }
        }
        const currentNode = getTargetBlockNode(state, this.options.specialMappings);
        if (!currentNode) return false;
        const parentNodeType = state.doc.resolve(currentNode.pos).parent.type.name;
        if (this.options.specialHandlers && this.options.specialHandlers[parentNodeType]?.outdent) {
          const specialHandler = this.options.specialHandlers[parentNodeType].outdent;
          const result = specialHandler?.({
            currentNode,
            node: currentNode,
            state,
            chain,
            editor
          });
          if (result) {
            return true;
          }
        }
        const $pos = state.doc.resolve(currentNode.pos);
        const parentNode = $pos.parent;
        const currentIndex = $pos.index($pos.depth);
        if (currentIndex < parentNode.childCount - 1) return true;
        let ancestorDepth = $pos.depth - 1;
        let previousAncestor = null;
        let previousAncestorDepth = 0;
        let ancestorFound = false;
        while (ancestorDepth >= 0 && !ancestorFound) {
          const ancestor = $pos.node(ancestorDepth);
          if (ancestorDepth > 1) {
            const ancestorParent = $pos.node(ancestorDepth - 1);
            const ancestorIndex = $pos.index(ancestorDepth - 1);
            if (ancestorIndex < ancestorParent.childCount - 1) {
              ancestorDepth--;
              continue;
            }
          }
          if (ancestor.type.contentMatch.matchType(currentNode.node.type) && ancestorDepth !== $pos.depth - 1) {
            ancestorFound = true;
            break;
          }
          previousAncestor = ancestor;
          previousAncestorDepth = ancestorDepth;
          ancestorDepth--;
        }
        if (ancestorFound && previousAncestor) {
          const insertPos = $pos.after(previousAncestorDepth);
          return chain().insertContentAt(insertPos, currentNode.node.content, {
            updateSelection: false
          }).deleteNode(currentNode.node.type).selectTextblockEnd().run();
        }
        return false;
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        this.editor.chain().focus().indent().run();
        return true;
      },
      "Shift-Tab": () => {
        this.editor.chain().focus().outdent().run();
        return true;
      }
    };
  }
});

export { IndentHandler as default };
//# sourceMappingURL=IndentHandler.js.map
